import { isDefined, isNotDefined } from '@sgme/fp';
import { format, isValid, parseISO } from 'date-fns';
import deepEqual from 'fast-deep-equal';
import { useState } from 'react';

import { selectCurrencyPairData } from '@/api/config/selectors.ts';
import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import {
  useAlgoSetIsModifyingField,
  useAlgoTileId,
  useValidateAlgo,
} from '@/App/tiles/orders/AlgoOrders/AlgoContext.tsx';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import { DatePickerWithTenorNext } from '@/components/ui/DatePickerWithTenorNext.tsx';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import { selectAlgoErrors, selectAlgoTile } from '@/store/orders/algoOrders/selectors.ts';

export const AlgoTenor = () => {
  const tileId = useAlgoTileId();
  const setIsModifyingField = useAlgoSetIsModifyingField();

  const [tenorOfDatePicker, setTenorOfDatePicker] = useState<string>();
  const track = useTrackEvent();

  const { tenor, currencyPair } = useAppSelector((state) => {
    const tile = selectAlgoTile(state, tileId);

    if (isNotDefined(tile)) {
      console.error(`[AlgoSpot] Tile with id ${tileId} not found in state.`);
      return {};
    }
    if (tile.type !== 'Twap+Order' && tile.type !== 'NightjarOrder' && tile.type !== 'FalconOrder') {
      console.error(`[AlgoSpot] Tile with id ${tileId} is not a AlgoOrder.`);
      return {};
    }

    return {
      tenor: tile.properties.tenor,
      currencyPair: `${tile.properties.currencyPair?.currency1}/${tile.properties.currencyPair?.currency2}`,
    };
  }, deepEqual);

  const currencyPairData = useAppSelector((state) => selectCurrencyPairData(state, currencyPair));

  const errors = useAppSelector((state) => selectAlgoErrors(state, tileId));

  const validateAlgo = useValidateAlgo();

  const handleDateChange = (dateOrTenor: string) => {
    track('Order action', 'Tenor changed');
    const isDateFormat = isValid(parseISO(dateOrTenor));

    if (!isDateFormat) {
      setTenorOfDatePicker(dateOrTenor);
      validateAlgo({
        tenor: dateOrTenor,
      });
    } else {
      setTenorOfDatePicker(undefined);
      validateAlgo({
        tenor: format(new Date(dateOrTenor), 'yyyy-MM-dd'),
      });
    }
  };

  const tenorDateError = errors?.tenor;

  return (
    <OrderRow labelId="order.fields.tenor.label" alignTop>
      <OmsFieldErrorWrapper errors={tenorDateError}>
        <DatePickerWithTenorNext
          date={isDefined(tenor) && isValid(parseISO(tenor)) ? format(parseISO(tenor), 'yyyy-MM-dd') : ''}
          tenor={tenorOfDatePicker}
          disabled={currencyPairData?.notDeliverable ?? false}
          data-e2e="tenor-date-input"
          onChange={handleDateChange}
          onFocus={() => setIsModifyingField(true)}
          onBlur={() => setIsModifyingField(false)}
          validationState={isDefined(tenorDateError) ? 'invalid' : undefined}
        />
      </OmsFieldErrorWrapper>
    </OrderRow>
  );
};
